import { isNull, isUndefined } from 'lodash';
import { useMemo } from 'react';
import { isButtonEnabled } from 'src/components/design-system/Button';
import { BlindReceivePermissions, CanReceiptUseNegativeQty } from 'src/constants/permissionsList';
import usePermission from 'src/hooks/usePermissions';
import { GLCodeDropdownData } from 'src/services/useGlCodesApi';
import { ReceiptLineItem } from 'src/services/useReceiptLineItemsApi';
export interface ReceiptLineItemError {
  currentDocQty: string[];
  item: string[];
  itemDescription: string[];
  uom: string[];
  poglCode: string[];
}

export interface ReceiptLineItemWarning {}

export interface ReceiptLineItemInfo {
  currentDocQty: string[];
  poQty: string[];
}

const defaultReceiptLineItemError: ReceiptLineItemError = {
  currentDocQty: [],
  item: [],
  itemDescription: [],
  uom: [],
  poglCode: [],
};

const defaultInvoiceLineItemInfo: ReceiptLineItemInfo = {
  currentDocQty: [],
  poQty: [],
};

const useReceiptLineItemErrors = (lineItems: ReceiptLineItem[], glOptions: GLCodeDropdownData) => {
  const isBlindReceive: boolean = usePermission({ permissions: BlindReceivePermissions });
  const canReceiptUseNegativeQty: boolean = usePermission({
    permissions: CanReceiptUseNegativeQty,
  });

  const errors = useMemo(
    () =>
      lineItems?.map((lineItem) => {
        const canEditLineItem = isButtonEnabled(lineItem?.canDeleteReceiptLineItem);
        let error: ReceiptLineItemError = { ...defaultReceiptLineItemError };
        if (
          !lineItem.currentDocQty?.toString()?.trim() ||
          isUndefined(!lineItem?.currentDocQty) ||
          isNull(!lineItem?.currentDocQty) ||
          lineItem?.currentDocQty.toString() === ''
        ) {
          error.currentDocQty = ['Current Doc Qty is required'];
        } else if (lineItem?.currentDocQty < 0 && !canReceiptUseNegativeQty) {
          error.currentDocQty = ['Current Doc Qty cannot be negative'];
        }

        if (canEditLineItem) {
          if (lineItem.item?.trim().length > 60) {
            error.item = ['Item is more than 60 characters'];
          }
          if (!lineItem.itemDescription?.trim()) {
            error.itemDescription = ['Item description is required'];
          } else if (lineItem.itemDescription?.trim().length > 4000) {
            error.itemDescription = ['Item description is more than 4000 characters'];
          }
          if (!lineItem?.uom?.trim()) {
            error.uom = ['UOM is required'];
          } else if (lineItem?.uom?.trim().length > 105) {
            error.uom = ['UOM is more than 105 characters'];
          }
          if (
            !lineItem?.poglCode?.toString()?.trim() ||
            lineItem?.poglCode.toString() === '' ||
            !glOptions.some((o) => o.value === lineItem.poglCode)
          ) {
            error.poglCode = ['Document has line items that do not have a GL code assigned.'];
          }
        }

        return error;
      }, []),
    [canReceiptUseNegativeQty, glOptions, lineItems]
  );

  const warnings = useMemo(
    () =>
      lineItems.map((lineItem) => {
        let warning: ReceiptLineItemWarning = {};

        return warning;
      }),
    [lineItems]
  );

  const infos = useMemo(
    () =>
      lineItems?.map((lineItem) => {
        let info: ReceiptLineItemInfo = { ...defaultInvoiceLineItemInfo };
        const receiptQuantity =
          !isBlindReceive && lineItem?.varianceQty < 0 && lineItem?.poQty >= 0;
        if (receiptQuantity) {
          info.currentDocQty = ['Quantity received exceeds quantity ordered'];
        }

        if (lineItem?.currentDocQty > lineItem?.poQty && lineItem?.poQty > 0 && !isBlindReceive) {
          info.currentDocQty = [...info.currentDocQty, 'Current Doc Qty is higher than PO Qty'];
          info.poQty = [...info.poQty, 'Current Doc Qty is higher than PO Qty'];
        }

        if (
          lineItem?.currentDocQty + lineItem?.previousDocQty > lineItem?.poQty &&
          lineItem?.poQty > 0 &&
          !isBlindReceive
        ) {
          info.currentDocQty = [...info.currentDocQty, 'Total Qty is higher than PO Qty'];
          info.poQty = [...info.poQty, 'Total Qty is higher than PO Qty'];
        }

        return info;
      }),
    [isBlindReceive, lineItems]
  );

  return { errors, infos, warnings };
};

export default useReceiptLineItemErrors;
